import { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, Loader2 } from 'lucide-react';
import { isVoid, ticketDate } from './ticketDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/parlays`;
const usd = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const hit = (l) => l.status === 'won' || l.color_token === 'green' || /HIT|WON/.test(String(l.performance_status || ''));
const dead = (l) => l.status === 'lost' || l.color_token === 'red' || /LOST|MISSED/.test(String(l.performance_status || ''));
const barColor = (p) => (p >= 100 ? 'bg-emerald-400' : p >= 75 ? 'bg-sky-400' : p >= 50 ? 'bg-purple-400' : 'bg-yellow-400');

// Active tickets only (still alive), % of legs already hit — closest to winning on top.
export const progressRows = (tickets) => tickets
  .filter((t) => !t.archived && ['pending', 'live', undefined, null].includes(t.status) && !(t.legs || []).some(dead))
  .map((t) => {
    const legs = (t.legs || []).filter((l) => !isVoid(l));
    const done = legs.filter(hit).length;
    return { t, done, total: legs.length, pct: legs.length ? Math.round((done / legs.length) * 100) : 0 };
  })
  .filter((r) => r.total)
  .sort((a, b) => b.pct - a.pct || (a.total - a.done) - (b.total - b.done) || (Number(b.t.potential_payout) || 0) - (Number(a.t.potential_payout) || 0));

const Row = ({ r, i }) => (
  <li className="space-y-0.5" data-testid={`progress-row-${r.t.id}`}>
    <div className="flex items-baseline justify-between gap-2 text-[11px]">
      <span className="min-w-0 truncate text-zinc-300">
        <b className="text-white">#{i + 1}</b> · {ticketDate(r.t)} · {r.total} legs{r.t.potential_payout ? ` · wins ${usd(r.t.potential_payout)}` : ''}
      </span>
      <span className="shrink-0 font-mono font-black text-white" data-testid={`progress-pct-${r.t.id}`}>{r.pct}% <span className="font-sans text-[10px] font-semibold text-zinc-400">{r.done}/{r.total} hit</span></span>
    </div>
    <div className="h-3 overflow-hidden rounded-full bg-zinc-800">
      <div className={`h-full rounded-full ${barColor(r.pct)} transition-[width] duration-700 ease-out ${r.pct >= 100 ? 'animate-pulse' : ''}`} style={{ width: `${Math.max(r.pct, 2)}%` }} />
    </div>
  </li>
);

export default function ActiveProgress({ refreshKey, tickets }) {
  const [fetched, setRows] = useState(null);
  const [all, setAll] = useState(false);
  const rows = tickets ? progressRows(tickets) : fetched;
  useEffect(() => {
    if (tickets) return undefined;  // guest tracker hands its own tickets in
    const get = () => axios.get(API).then((r) => setRows(progressRows(Array.isArray(r.data) ? r.data : r.data.parlays || []))).catch(() => setRows([]));
    get();
    const id = setInterval(get, 30000);
    window.addEventListener('ticket-money-changed', get);
    return () => { clearInterval(id); window.removeEventListener('ticket-money-changed', get); };
  }, [refreshKey, tickets]);
  const shown = rows && (all ? rows : rows.slice(0, 8));
  return (
    <section className="space-y-2 rounded-xl border border-zinc-800 bg-zinc-900/70 p-3" data-testid="active-progress">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-black"><Activity className="h-4 w-4 text-amber-400" />Active tickets — how close to winning</p>
        {rows && <span className="text-[11px] text-zinc-400" data-testid="active-progress-count">{rows.length} alive</span>}
      </div>
      {!rows ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-zinc-500" /> : !rows.length ? (
        <p className="py-2 text-center text-sm text-zinc-500" data-testid="active-progress-empty">No active tickets right now.</p>
      ) : (
        <>
          <ul className="space-y-2">{shown.map((r, i) => <Row key={r.t.id} r={r} i={i} />)}</ul>
          <p className="text-[10px] text-zinc-500">% = legs already hit · purple 50%+ · blue 75%+ · green = every leg hit</p>
          {rows.length > 8 && (
            <button type="button" onClick={() => setAll(!all)} className="text-[11px] font-bold text-amber-300 hover:text-amber-200" data-testid="active-progress-toggle">
              {all ? 'Show top 8' : `Show all ${rows.length}`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
